import { useMemo } from 'react';
import useClientStore from '@/store/useClientStore';
import { currencyConfig, getCurrencySymbol } from '@/lib/currency';
import { isUsa } from '@/lib/country';

/**
 * Hook canónico de moneda del tenant (fase 2 Brasil: delega la config al módulo
 * puro '@/lib/currency'). Para CLP y USD el output es byte-idéntico al histórico
 * (fijado por el harness test:currency del repo goautos-admin, del que este hook
 * es copia dual-repo); BRL ahora formatea pt-BR/R$ en vez de caer a es-CL.
 */
export const useCurrency = () => {
  const { client } = useClientStore();

  // Con el país: un tenant de EE.UU. en USD formatea sin centavos ("$24,990").
  // Cualquier otro país (incluido el chileno que vende en USD) = lo de siempre.
  const { code: currency, locale, decimals, maxDecimals } = currencyConfig(client?.currency, client?.country);
  // El tope de decimales SOLO para EE.UU.: COP/ARS también lo definen y este
  // hook nunca lo aplicó (su salida queda byte-idéntica).
  const sinCentavosUsa = isUsa(client?.country) && maxDecimals === 0;

  const formatPrice = useMemo(() => {
    return (price: number, options?: Intl.NumberFormatOptions) => {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: decimals,
        ...(sinCentavosUsa ? { maximumFractionDigits: 0 } : {}),
        ...options,
      }).format(price);
    };
  }, [locale, currency, decimals, sinCentavosUsa]);

  const symbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  return {
    currency,
    locale,
    formatPrice,
    /** Símbolo de la moneda ('$' CLP/USD, 'R$' BRL) — para inputs y prefijos. */
    symbol,
    /** Decimales de display de la moneda (CLP 0, USD 2, BRL 0 en fase 1). */
    decimals,
  };
};
